import React, { useState, useEffect, useMemo } from "react";
import "./Testdpprow.css";
import { GET, POST } from "../../Api/HttpsMethods";
import EndPoints from "../../Api/EndPoints";
import StudentViewAllModal from "./StudentViewAllModal";

// ── Color map ─────────────────────────────────────────────────────────────────
const SUBJECT_COLOR_MAP = {
  Physics:   "#e05c5c",
  Chemistry: "#f0a500",
  Biology:   "#27ae60",
  Zoology:   "#5b8dee",
  Botany:    "#9b59b6",
};

function parseTableData(table_data) {
  const headers = [];
  const rows    = [];
  table_data.forEach((item) => {
    if (typeof item === "string") {
      const type  = item.startsWith("n*") ? "number" : "text";
      const label = item.slice(2);
      headers.push({ label, type });
    } else {
      rows.push(item);
    }
  });
  return { headers, rows };
}

// ─── Circular Score Ring ──────────────────────────────────────────────────────
function ScoreRing({ value, color }) {
  const R            = 15;
  const circumference = 2 * Math.PI * R;
  const filled       = (value / 100) * circumference;
  return (
    <div className="score-ring-wrap">
      <svg className="score-ring-svg" viewBox="0 0 38 38">
        <circle cx={19} cy={19} r={R} fill="none" stroke="#f0f0f6" strokeWidth={4} />
        <circle
          cx={19} cy={19} r={R}
          fill="none"
          stroke={color}
          strokeWidth={4}
          strokeDasharray={`${filled} ${circumference - filled}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="score-ring-text" style={{ color }}>{value}%</span>
    </div>
  );
}

function getRankClass(rank) {
  if (rank <= 15) return "rank-low";
  if (rank <= 20) return "rank-mid";
  return "rank-high";
}

function TestPerformanceCard({ progressCard = [], clientBranchId }) {
  const SUBJECTS = ["Physics", "Chemistry", "Botany", "Zoology"];

  const [selectedTs, setSelectedTs] = useState("all");
  const [modalOpen, setModalOpen]   = useState(false);

  const testSeriesList = useMemo(() => {
    const seen = new Set();
    const list = [];
    progressCard.forEach((row) => {
      if (row.ts_id && !seen.has(row.ts_id)) {
        seen.add(row.ts_id);
        list.push({ ts_id: row.ts_id, ts_name: row.ts_name });
      }
    });
    return list;
  }, [progressCard]);

  const filteredProgress = progressCard.filter((row) => {
    if (selectedTs === "all") return true;
    return String(row.ts_id) === String(selectedTs);
  });

  const activeSubjects = SUBJECTS.filter((s) =>
    filteredProgress.some((r) => r[`${s}_total_marks`] !== null)
  );

  // ── Completed / Total counts (AB = Overall_total_marks is null/undefined) ──
  // const displayCompleted = filteredProgress.filter(
  //   (r) => r.Overall_total_marks !== null && r.Overall_total_marks !== undefined
  // ).length;
  // const displayTotal = filteredProgress.length;


  // ── Completed / Total counts (AB = Overall_total_marks is "Ab"/null/undefined) ──
  const isAbsentRow = (r) =>
    r.Overall_total_marks === null ||
    r.Overall_total_marks === undefined ||
    r.Overall_total_marks === "Ab";

  const displayCompleted = filteredProgress.filter((r) => !isAbsentRow(r)).length;
  const displayTotal = filteredProgress.length;

  return (
    <div className="test-dpp-card">
      <div className="test-card-header">
        <div>
          <h3 className="td-card-title">
            Test Performance&nbsp;
            <span style={{ fontWeight: 500, fontSize: "13px", color: "#555" }}>
              ({displayCompleted}/{displayTotal})
            </span>
          </h3>
        </div>

        <select
          className="test-filter-btn dpp-subject-select"
          value={selectedTs}
          onChange={(e) => setSelectedTs(e.target.value)}
        >
          <option value="all">All</option>
          {testSeriesList.map((ts) => (
            <option key={ts.ts_id} value={ts.ts_id}>{ts.ts_name}</option>
          ))}
        </select>

        <button className="test-view-all" onClick={() => setModalOpen(true)}>
          View all
        </button>
      </div>

      <div className="dpp-table-scroll">
        {filteredProgress.length === 0 ? (
          <p className="dpp-empty-text">No test data available</p>
        ) : (
          <table className="dpp-table-new-student-dashboard" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Test Name</th>
                <th>Test Series</th>
                {activeSubjects.map((s) => <th key={s}>{s}</th>)}
                <th>Total Marks</th>
                <th>Test Date</th>
                <th>Rank</th>
              </tr>
            </thead>
            <tbody>
              {filteredProgress.map((row, i) => {
                const date = row.test_date
                  ? new Date(row.test_date).toLocaleDateString("en-GB", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—";
                // const isAbsent = row.Overall_total_marks === null || row.Overall_total_marks === undefined;
                const isAbsent = isAbsentRow(row);
                return (
                  <tr key={i}>
                    <td className="dpp-date dpp-name-cell" title={row.test_name ?? ""}>
                      {row.test_name ?? "—"}
                    </td>
                    <td className="dpp-date dpp-name-cell" title={row.ts_name ?? ""}>
                      {row.ts_name ?? "—"}
                    </td>
                    {activeSubjects.map((s) => {
                      const val = row[`${s}_total_marks`];
                      return (
                        <td key={s} className="dpp-date">
                          {val !== null && val !== undefined ? val : "—"}
                        </td>
                      );
                    })}
                    <td className="dpp-date">
                      {/* {isAbsent ? "AB" : row.Overall_total_marks} */}
                      {row.Overall_total_marks}
                    </td>
                    <td className="dpp-date">{date}</td>
                    <td className="dpp-date">
                      {isAbsent ? "AB" : (row.Overall_rank !== null ? row.Overall_rank : "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <StudentViewAllModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Test Performance"
        type="test"
        data={progressCard}
        activeSubjects={activeSubjects}
      />
    </div>
  );
}


function DppTrackerCard({ courseId, dppReport = [] }) {
  const [subjects, setSubjects]               = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [modalOpen, setModalOpen]             = useState(false);

  const EXCLUDED_COLS  = ["Photo", "Student ID", "Gender ID", "Student Name", "Roll Number", "Batch Name"];
  const DATE_ONLY_COLS = ["DPP Window Start", "Actual Start Time"];

  // ── Fetch subjects for dropdown ──────────────────────────────────────────
  useEffect(() => {
    if (!courseId) return;
    (async () => {
      const data = await GET.withToken(
        EndPoints.GetSubjectsByCourse + `?course_id=${courseId}`
      );
      if (Array.isArray(data))            setSubjects(data);
      else if (Array.isArray(data?.data)) setSubjects(data.data);
      else                                setSubjects([]);
    })();
  }, [courseId]);

  // ── Parse headers ────────────────────────────────────────────────────────
  const headerRow = dppReport[0] ?? [];
  const dataRows  = dppReport.slice(1);

  const allHeaders = headerRow.map((item, idx) => ({
    label:         item.slice(2),
    key:           item.slice(2).toLowerCase().replace(/ /g, "_"),
    type:          item.startsWith("n*") ? "number" : "text",
    originalIndex: idx,
  }));

  const filteredHeaders = allHeaders.filter((h) => !EXCLUDED_COLS.includes(h.label));
  const subjectColIndex = allHeaders.find((h) => h.label === "Subject")?.originalIndex ?? -1;
  const statusColIndex  = allHeaders.find((h) => h.label === "Status")?.originalIndex  ?? -1;

  // ── Helpers ──────────────────────────────────────────────────────────────
  function isCompleted(row) {
    const status = row[statusColIndex];
    const s = (Array.isArray(status) ? status[0] : status ?? "").toLowerCase();
    return s === "completed";
  }

  function rowMatchesSubject(row) {
    if (selectedSubject === "all") return true;
    const subjectVal   = row[subjectColIndex];
    const subjectArr   = Array.isArray(subjectVal) ? subjectVal : [subjectVal];
    const selectedName = subjects.find(
      (s) => String(s.subject_id) === String(selectedSubject)
    )?.subject_name ?? "";
    return subjectArr.some((s) => s?.toLowerCase() === selectedName.toLowerCase());
  }

  function formatDateOnly(val) {
    if (!val || val === "—") return val;
    const d = new Date(val);
    if (isNaN(d)) return val;
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  }

  // ── Filtered rows ────────────────────────────────────────────────────────
  const filteredRows = dataRows.filter(rowMatchesSubject);

  // ── Completed / Total counts (reactive to selected subject) ─────────────
  const displayCompleted = filteredRows.filter(isCompleted).length;
  const displayTotal     = filteredRows.length;

  // ── Selected subject name (for title) ────────────────────────────────────
  const selectedSubjectName = selectedSubject === "all"
    ? ""
    : subjects.find((s) => String(s.subject_id) === String(selectedSubject))?.subject_name ?? "";

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="test-dpp-card">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="dpp-card-header">
        <div>
          <h3 className="td-card-title">
            DPP Tracker&nbsp;
            <span style={{ fontWeight: 500, fontSize: "13px", color: "#555" }}>
              {/* {selectedSubjectName && `${selectedSubjectName} `} */}
              ({displayCompleted}/{displayTotal})
            </span>
          </h3>
        </div>

        <select
          className="test-filter-btn dpp-subject-select"
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
        >
          <option value="all">All</option>
          {subjects.map((s) => (
            <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
          ))}
        </select>

        <button className="test-view-all" onClick={() => setModalOpen(true)}>
          View all
        </button>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="dpp-table-scroll">
        {filteredRows.length === 0 ? (
          <p className="dpp-empty-text">No DPP data available</p>
        ) : (
          <table className="dpp-table-new-student-dashboard" style={{ width: "100%" }}>
            <thead>
              <tr>{filteredHeaders.map((h) => <th key={h.key}>{h.label}</th>)}</tr>
            </thead>
            <tbody>
              {filteredRows.map((row, i) => (
                <tr key={i}>
                  {filteredHeaders.map((h) => {
                    let val = row[h.originalIndex];
                    if (Array.isArray(val)) val = val.join(", ");
                    val = val ?? "—";
                    if (DATE_ONLY_COLS.includes(h.label)) val = formatDateOnly(val);
                    const isNameCol = h.label === "DPP Name";
                    return (
                      <td
                        key={h.key}
                        className={`dpp-date${isNameCol ? " dpp-name-cell" : ""}`}
                        title={isNameCol ? val : undefined}
                      >
                        {val}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      <StudentViewAllModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="DPP Tracker — All Records"
        type="dpp"
        data={dppReport}
      />
    </div>
  );
}

export default function TestDppRow({ studentId, batchId, sessionId, courseId, progressCard, dppReport, clientBranchId}) {
  return (
    <div className="test-dpp-row">
      <TestPerformanceCard progressCard={progressCard} clientBranchId={clientBranchId}/>
      <DppTrackerCard
        studentId={studentId}
        batchId={batchId}
        sessionId={sessionId}
        courseId={courseId}  
        dppReport={dppReport}
      />
    </div>
  );
}





// function TestPerformanceCard({ progressCard = [], clientBranchId }) {
//   const SUBJECTS = ["Physics", "Chemistry", "Botany", "Zoology"];
 
//   // const [testSeriesList, setTestSeriesList] = useState([]);
//   const [selectedTs, setSelectedTs]         = useState("all");
//   const [modalOpen, setModalOpen]           = useState(false);   // ← NEW
 
//   // useEffect(() => {
//   //   if (!clientBranchId) return;
//   //   (async () => {
//   //     const data = await GET.withToken(
//   //       EndPoints.GetTestSeriesListByBranch + `?client_branch_id=${clientBranchId}`
//   //     );
//   //     const list = Array.isArray(data) ? data
//   //                : Array.isArray(data?.data) ? data.data
//   //                : [];
//   //     setTestSeriesList(list);
//   //   })();
//   // }, [clientBranchId]);
 
//   const testSeriesList = useMemo(() => {
//     const seen = new Set();
//     const list = [];
//     progressCard.forEach((row) => {
//       if (row.ts_id && !seen.has(row.ts_id)) {
//         seen.add(row.ts_id);
//         list.push({ ts_id: row.ts_id, ts_name: row.ts_name });
//       }
//     });
//     return list;
//   }, [progressCard]);

//   const filteredProgress = progressCard.filter((row) => {
//     if (selectedTs === "all") return true;
//     return String(row.ts_id) === String(selectedTs);
//   });
 
//   const activeSubjects = SUBJECTS.filter((s) =>
//     filteredProgress.some((r) => r[`${s}_total_marks`] !== null)
//   );
 
//   return (
//     <div className="test-dpp-card">
//       <div className="test-card-header">
//         <div>
//           <h3 className="td-card-title">Test Performance</h3>
//         </div>
 
//         <select
//           className="test-filter-btn dpp-subject-select"
//           value={selectedTs}
//           onChange={(e) => setSelectedTs(e.target.value)}
//         >
//           <option value="all">All</option>
//           {testSeriesList.map((ts) => (
//             <option key={ts.ts_id} value={ts.ts_id}>{ts.ts_name}</option>
//           ))}
//         </select>
 
//         {/* ← changed: onClick opens modal */}
//         <button className="test-view-all" onClick={() => setModalOpen(true)}>
//           View all
//         </button>
//       </div>
 
//       <div className="dpp-table-scroll">
//         {filteredProgress.length === 0 ? (
//           <p className="dpp-empty-text">No test data available</p>
//         ) : (
//           <table className="dpp-table-new-student-dashboard" style={{ width: "100%" }}>
//             <thead>
//               <tr>
//                 <th>Test Name</th>
//                 <th>Test Series</th>
//                 {activeSubjects.map((s) => <th key={s}>{s}</th>)}
//                 <th>Total Marks</th>
//                 <th>Test Date</th>
//                 <th>Rank</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredProgress.map((row, i) => {
//                 const date = row.test_date
//                   ? new Date(row.test_date).toLocaleDateString("en-GB", {
//                       day: "2-digit", month: "short", year: "numeric",
//                     })
//                   : "—";
//                 return (
//                   <tr key={i}>
//                     <td className="dpp-date dpp-name-cell" title={row.test_name ?? ""}>
//                       {row.test_name ?? "—"}
//                     </td>
//                      <td className="dpp-date dpp-name-cell" title={row.ts_name ?? ""}>  {/* ← ADD */}
//                       {row.ts_name ?? "—"}
//                     </td>
//                     {activeSubjects.map((s) => {
//                       const val = row[`${s}_total_marks`];
//                       return <td key={s} className="dpp-date">{val !== null ? val : "—"}</td>;
//                     })}
//                     <td className="dpp-date">{row.Overall_total_marks !== null ? row.Overall_total_marks : "—"}</td>
//                     <td className="dpp-date">{date}</td>
//                     <td className="dpp-date">{row.Overall_rank !== null ? row.Overall_rank : "—"}</td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>
 
//       {/* ← NEW: modal — passes ALL progressCard rows (not filtered) */}
//       <StudentViewAllModal
//         isOpen={modalOpen}
//         onClose={() => setModalOpen(false)}
//         title="Test Performance — All Tests"
//         type="test"
//         data={progressCard}            // full unfiltered data
//         activeSubjects={activeSubjects}
//       />
//     </div>
//   );
// }
